import { memo, useMemo } from 'react'

import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import {
  IAgoraRTCRemoteUser,
  IRemoteVideoTrack,
  RemoteAudioTrack,
  RemoteVideoTrack,
} from 'agora-rtc-react'
import { ScreenShareOn } from 'src/components/icon'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'

import { useVideoCallUsers } from '../../api/getVideoCallUsers'
import { extractOriginalUserUidFromScreenShareUserUid } from '../../utils/screenShare'
type SharedScreenPlayerProps = {
  colorScheme?: 'blue' | 'green'
  isSmall?: boolean
  screenSharingRemoteUser: IAgoraRTCRemoteUser
  currentUserUid?: number
}

export const SharedScreenPlayer: React.FC<SharedScreenPlayerProps> = ({
  colorScheme = 'blue',
  isSmall = false,
  screenSharingRemoteUser,
  currentUserUid,
}) => {
  const videoTrack = useMemo(() => {
    return screenSharingRemoteUser.videoTrack
  }, [screenSharingRemoteUser.videoTrack])

  // 画面共有UIDから元のユーザーUIDを抽出してユーザー名を取得
  const originalUserUid = useMemo(() => {
    try {
      return extractOriginalUserUidFromScreenShareUserUid(
        screenSharingRemoteUser.uid,
      )
    } catch {
      return null
    }
  }, [screenSharingRemoteUser.uid])

  const { data: users } = useVideoCallUsers({
    videoCallUserUids: originalUserUid ? [originalUserUid] : [],
  })

  const userName = users?.[0]?.name

  // 現在のユーザーが画面共有者かどうかを判定
  const isCurrentUserSharingScreen = useMemo(() => {
    if (!currentUserUid || !originalUserUid) return false
    return currentUserUid === originalUserUid
  }, [currentUserUid, originalUserUid])

  return (
    <>
      <Flex
        direction="column"
        boxSize="full"
        border={isSmall ? '4px solid' : '6px solid'}
        borderColor={`${colorScheme}.500`}
        bg={`${colorScheme}.500`}
      >
        <Grid
          alignItems="center"
          h={isSmall ? '24px' : '36px'}
          pb="6px"
          color="white"
          gap="4px"
          templateColumns="16px minmax(0, 1fr)"
          mx="auto"
        >
          <ScreenShareOn />
          <Flex fontSize="sm" fontWeight="bold">
            <Text
              as="span"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {userName || screenSharingRemoteUser.uid}
            </Text>
            <Text as="span" wordBreak="keep-all">
              が画面を共有中
            </Text>
          </Flex>
        </Grid>
        <Box flex="1">
          <RemoteVideoPlayer track={videoTrack} />
          <RemoteAudioTrack play={!isCurrentUserSharingScreen} />
        </Box>
      </Flex>
    </>
  )
}

// memo化することで音声のOn/Offを切り替えた際などのちらつきを防いでいる
const RemoteVideoPlayer: React.FC<{ track: IRemoteVideoTrack | undefined }> =
  memo(function RemoteVideoPlayer({ track }) {
    if (!track) {
      return <LoadingDots dotColor="white" />
    }
    return (
      <RemoteVideoTrack
        play
        track={track}
        videoPlayerConfig={{ fit: 'contain' }}
      />
    )
  })
